// eslint-disable-next-line no-use-before-define
import React from 'react';
import { Button, CircularProgress, CustomTheme, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_USER_PROFILE } from 'op-graphql/queries';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';

import './NotFoundPage.scss';

const HOME_REDIRECT_PATH = '/server/auth/redirect';

const FullPageSpinner = ({ label }: { label: string }): JSX.Element => (
  <Stack width={1} height={1} justifyContent="center" direction="row" padding={3} alignItems="center" gap={2}>
    <CircularProgress color="primary" />
    <Typography>{label}</Typography>
  </Stack>
);

const NotFoundCard = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const theme: CustomTheme = useTheme();
  return (
    <div id="not-found-page">
      <div id="not-found-container">
        <div>
          {theme.custom.darkLogo}
          {children}
        </div>
        <div id="not-found-logo">{theme.custom.logoOnly}</div>
      </div>
    </div>
  );
};

const NotFoundPage = (): JSX.Element => {
  const { data, loading, error } = useQuery(GET_USER_PROFILE);

  if (loading) {
    return <FullPageSpinner label="Loading..." />;
  }

  if (error) {
    // 401/403 responses are redirected to /sso/login by the Apollo error link; any other
    // failure (network, 5xx) lands here — don't treat it as unauthenticated
    return (
      <NotFoundCard>
        <div id="not-found-title">{'Something went wrong'}</div>
        <div id="not-found-detail" data-testid="not-found-error">
          <span>We were unable to load this page. Please refresh the page, or contact Innovation Support at:</span>
          &nbsp;
          <a href={`mailto:${CurrentAppConfig.SupportEmail}`}>{CurrentAppConfig.SupportEmail}</a>
        </div>
      </NotFoundCard>
    );
  }

  if (!data?.user) {
    // /sso/login is a server endpoint, not an SPA route — a client-side redirect would be
    // swallowed by the catch-all route, so it needs a full page navigation
    window.location.replace(`/sso/login?next=${HOME_REDIRECT_PATH}`);
    return <FullPageSpinner label="Redirecting..." />;
  }

  return (
    <NotFoundCard>
      <div id="not-found-title">{'Page not found'}</div>
      <div id="not-found-detail">
        <span>The page you are looking for does not exist or may have been moved.</span>
        <span>
          &nbsp;If you believe you are seeing this screen in error, please contact Innovation Support by email at:
        </span>
        &nbsp;
        <a href={`mailto:${CurrentAppConfig.SupportEmail}`}>{CurrentAppConfig.SupportEmail}</a>
      </div>
      <Button
        href={HOME_REDIRECT_PATH}
        variant="contained"
        data-testid="not-found-home-button"
        sx={{ marginTop: '32px' }}>
        Return to home
      </Button>
    </NotFoundCard>
  );
};

export default NotFoundPage;
